/**
 * NutSheetTable — sticky-header editable grid + "+ Add Row" button with a
 * day-sheet subtotal strip (Est./Actual Gross, F&I, Back End $, Holdback,
 * Doc — visible rows only) on the same bar.
 * 13 columns. Auto-populated sold-unit fields (incl. Days) collapse into the
 * blue "Deal Info" cell; auto-populated trade-in fields collapse into the
 * amber "Trade Info" cell; per-trade ACV/Retail/Plan collapse into the amber
 * "Trade $" cell as stacked T1/T2 sub-rows.
 */

import React from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { NutSheetRow } from './NutSheetRow';
import { formatCurrency } from './formatters';
import { tradeDollarGrid } from './TradeFinancialsBlock';
import {
  ExistingNutSheetMatch,
  isSwapRow,
  isValueCenterRow,
  isWholesaleRow,
  DealLookupResponse,
  NutSheetRowDraft,
} from '../../types/nutSheet';

interface NutSheetTableProps {
  rows: NutSheetRowDraft[];
  locationId: number;
  savingByIndex: Record<number, boolean>;
  /** When false, wholesale (USED type "W") rows are hidden from the grid. */
  showWholesale: boolean;
  onToggleWholesale: () => void;
  /** When true, ONLY wholesale (USED "W") rows render; overrides the other toggles. */
  showOnlyWholesale: boolean;
  onToggleOnlyWholesale: () => void;
  /** When true, ONLY Value Center (USED type ending "VC") rows render; overrides the other toggles. */
  showOnlyValueCenter: boolean;
  onToggleOnlyValueCenter: () => void;
  /** When true, Value Center (USED type ending "VC") rows are hidden from the grid. */
  excludeValueCenter: boolean;
  onToggleExcludeValueCenter: () => void;
  /** When false, swap (NEW type "S") rows are hidden from the grid. */
  showSwap: boolean;
  onToggleSwap: () => void;
  /** When true, each row's F&I cell + the MTD F&I summary show net of finance reserve. */
  excludeFiReserve: boolean;
  onToggleExcludeFiReserve: () => void;
  /** Accounting/Admin get the editable "+ Comment" affordance; others read-only. */
  canEditAccountingComment: boolean;
  onChangeRow: (index: number, patch: Partial<NutSheetRowDraft>) => void;
  onCommitRow: (index: number) => void;
  onDeleteRow: (index: number) => void;
  onAddRow: () => void;
  onFocusRow: (clientId: string) => void;
  onBlurRow: () => void;
  onResolveConflict: (index: number, action: 'reload' | 'keepMine') => void;
  onResolveDeleted: (index: number, action: 'recreate' | 'discard') => void;
  onShowHistory: (index: number) => void;
  onDuplicateDeal: (
    index: number,
    result: DealLookupResponse,
    matches: ExistingNutSheetMatch[],
  ) => void;
}

const headerClass = 'sticky top-0 bg-gray-100 px-1 py-2 text-[11px] font-semibold text-gray-700 whitespace-nowrap';
const SOLD_HDR = `${headerClass} bg-blue-100`;
const TRADE_HDR = `${headerClass} bg-amber-100`;

/**
 * A single "Show X (N)" row-visibility checkbox; disabled when count is 0 or
 * when explicitly disabled (e.g. another filter overrides it).
 */
const FilterToggle: React.FC<{
  label: string;
  noun: string;
  count: number;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}> = ({ label, noun, count, checked, onChange, disabled = false }) => {
  const isDisabled = disabled || count === 0;
  return (
    <label
      className={`inline-flex items-center gap-2 text-xs font-medium select-none ${
        isDisabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 cursor-pointer'
      }`}
      title={
        disabled
          ? `Disabled while a conflicting filter is active`
          : count === 0
            ? `No ${noun} rows on this day`
            : `Show or hide ${noun} rows`
      }
    >
      <input
        type="checkbox"
        className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        checked={checked}
        disabled={isDisabled}
        onChange={onChange}
      />
      {label}
      {count > 0 ? ` (${count})` : ''}
    </label>
  );
};

export const NutSheetTable: React.FC<NutSheetTableProps> = ({
  rows,
  locationId,
  savingByIndex,
  showWholesale,
  onToggleWholesale,
  showOnlyWholesale,
  onToggleOnlyWholesale,
  showOnlyValueCenter,
  onToggleOnlyValueCenter,
  excludeValueCenter,
  onToggleExcludeValueCenter,
  showSwap,
  onToggleSwap,
  excludeFiReserve,
  onToggleExcludeFiReserve,
  canEditAccountingComment,
  onChangeRow,
  onCommitRow,
  onDeleteRow,
  onAddRow,
  onFocusRow,
  onBlurRow,
  onResolveConflict,
  onResolveDeleted,
  onShowHistory,
  onDuplicateDeal,
}) => {
  const wholesaleCount = rows.filter(isWholesaleRow).length;
  const valueCenterCount = rows.filter(isValueCenterRow).length;
  const swapCount = rows.filter(isSwapRow).length;
  // Rows hidden by the active toggles (wholesale = USED "W", value center =
  // USED "*VC", swap = NEW "S" — all disjoint, so no double count). Filtering
  // happens in the map below (not here) so each NutSheetRow keeps its true index
  // for callbacks; these counts just drive the empty-state message. The two
  // "show only" filters are mutually exclusive (each disables the other in the UI).
  const visibleCount = showOnlyWholesale
    ? wholesaleCount
    : showOnlyValueCenter
      ? valueCenterCount
      : rows.length -
        ((showWholesale ? 0 : wholesaleCount) +
          (excludeValueCenter ? valueCenterCount : 0) +
          (showSwap ? 0 : swapCount));
  const hiddenCount = rows.length - visibleCount;

  // Mirrors the render skip in the row map below — a row hidden by the filters
  // is excluded from the day subtotal so the strip always matches what's on screen.
  const isRowVisible = (row: NutSheetRowDraft): boolean => {
    if (showOnlyWholesale) return isWholesaleRow(row);
    if (showOnlyValueCenter) return isValueCenterRow(row);
    if (!showWholesale && isWholesaleRow(row)) return false;
    if (excludeValueCenter && isValueCenterRow(row)) return false;
    if (!showSwap && isSwapRow(row)) return false;
    return true;
  };

  // Day-sheet subtotals for the six money columns, visible rows only. F&I nets
  // out each row's finance reserve when the Exclude-F&I-Reserve toggle is on,
  // matching the per-row net hint and the MTD summary card.
  //
  // `units` counts vehicles sold that day: one per real (typed) row, but
  // Wholesale (USED "W") and Swap (NEW "S") are excluded — matching the
  // server-side retail unit count in lambda/nut-sheet/shared/summarize.ts. Value
  // Center rows still count as units (they're retail used sales). Empty draft
  // rows (no type yet) don't count.
  const subtotals = rows.filter(isRowVisible).reduce(
    (acc, row) => {
      acc.estimatedGross += row.estimatedGross ?? 0;
      acc.actualGross += row.actualGross ?? 0;
      acc.fAndI += (row.fAndI ?? 0) - (excludeFiReserve ? (row.financeReserve ?? 0) : 0);
      acc.points += row.points ?? 0;
      acc.holdback += row.holdback ?? 0;
      acc.doc += row.doc ?? 0;
      if (row.type && !isWholesaleRow(row) && !isSwapRow(row)) acc.units += 1;
      return acc;
    },
    { estimatedGross: 0, actualGross: 0, fAndI: 0, points: 0, holdback: 0, doc: 0, units: 0 },
  );

  return (
    <div className="bg-white rounded-lg shadow-md mb-4">
      <div className="flex items-center justify-end gap-4 px-4 py-2 border-b border-gray-100">
        <FilterToggle
          label="Show only Wholesale"
          noun="wholesale (USED type W)"
          count={wholesaleCount}
          checked={showOnlyWholesale}
          onChange={onToggleOnlyWholesale}
          disabled={showOnlyValueCenter}
        />
        <FilterToggle
          label="Show only Value Center"
          noun="Value Center (USED type *VC)"
          count={valueCenterCount}
          checked={showOnlyValueCenter}
          onChange={onToggleOnlyValueCenter}
          disabled={showOnlyWholesale || excludeValueCenter}
        />
        <FilterToggle
          label="Exclude Value Center"
          noun="Value Center (USED type *VC)"
          count={valueCenterCount}
          checked={excludeValueCenter}
          onChange={onToggleExcludeValueCenter}
          disabled={showOnlyWholesale || showOnlyValueCenter}
        />
        <FilterToggle
          label="Show Wholesale"
          noun="wholesale (USED type W)"
          count={wholesaleCount}
          checked={showWholesale}
          onChange={onToggleWholesale}
          disabled={showOnlyWholesale || showOnlyValueCenter}
        />
        <FilterToggle
          label="Show Swap"
          noun="swap (NEW type S)"
          count={swapCount}
          checked={showSwap}
          onChange={onToggleSwap}
          disabled={showOnlyWholesale || showOnlyValueCenter}
        />
        {/* Not row-visibility — a display toggle. When checked, every row's F&I
            shows net of its finance reserve and the MTD F&I summary recomputes. */}
        <label
          className="inline-flex items-center gap-2 text-xs font-medium select-none text-gray-700 cursor-pointer"
          title="Show F&I net of each deal's finance reserve (for pay plans paid on F&I minus reserve)"
        >
          <input
            type="checkbox"
            className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={excludeFiReserve}
            onChange={onToggleExcludeFiReserve}
          />
          Exclude F&amp;I Reserve
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className={headerClass}>Type</th>
              <th className={headerClass} style={{ minWidth: '110px' }}>Deal # / Stock #</th>
              <th className={SOLD_HDR} style={{ minWidth: '260px' }}>Deal Info</th>
              <th className={headerClass}>Est. Gross</th>
              <th className={headerClass}>Actual Gross</th>
              <th className={headerClass} style={{ minWidth: '96px' }}>F&amp;I</th>
              <th className={headerClass}>Back End $</th>
              <th className={headerClass}>Holdback</th>
              <th className={headerClass} style={{ minWidth: '80px' }}>Doc</th>
              <th className={headerClass}>Finance Source</th>
              <th className={TRADE_HDR} style={{ minWidth: '240px' }}>Trade Info</th>
              {/* Labels share the cell's grid template (tradeDollarGrid) so
                  each sits exactly over its input column, styled like the
                  other bold headers. */}
              <th className={TRADE_HDR} style={{ minWidth: '250px' }}>
                <div className={tradeDollarGrid}>
                  <span />
                  <span>Trade ACV</span>
                  <span>Trade Retail</span>
                  <span>Trade Plan</span>
                </div>
              </th>
              <th className={headerClass}></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={13} className="px-4 py-6 text-center text-gray-500 italic">
                  No transactions yet for this day. Click "Add Row" to start.
                </td>
              </tr>
            )}
            {rows.length > 0 && visibleCount === 0 && (
              <tr>
                <td colSpan={13} className="px-4 py-6 text-center text-gray-500 italic">
                  {hiddenCount} {hiddenCount === 1 ? 'row is' : 'rows are'} hidden by the filters
                  above. Re-check a box to view {hiddenCount === 1 ? 'it' : 'them'}.
                </td>
              </tr>
            )}
            {rows.map((row, idx) => {
              // Skip (don't filter) hidden rows so each rendered row keeps its
              // true array index for the index-based callbacks.
              if (!isRowVisible(row)) return null;
              return (
                <NutSheetRow
                  key={row.clientId}
                  row={row}
                  locationId={locationId}
                  onChange={(patch) => onChangeRow(idx, patch)}
                  onCommit={() => onCommitRow(idx)}
                  onDelete={() => onDeleteRow(idx)}
                  onFocusRow={() => onFocusRow(row.clientId)}
                  onBlurRow={onBlurRow}
                  onResolveConflict={(action) => onResolveConflict(idx, action)}
                  onResolveDeleted={(action) => onResolveDeleted(idx, action)}
                  onShowHistory={() => onShowHistory(idx)}
                  onDuplicateDeal={(result, matches) => onDuplicateDeal(idx, result, matches)}
                  saving={!!savingByIndex[idx]}
                  excludeFiReserve={excludeFiReserve}
                  canEditAccountingComment={canEditAccountingComment}
                />
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-3 border-t border-gray-200">
        <button
          onClick={onAddRow}
          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Row
        </button>
        {visibleCount > 0 && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm font-bold text-gray-900 tabular-nums">
            <span><span className="text-gray-500">Units:</span> {subtotals.units}</span>
            <span><span className="text-gray-500">Est. Gross:</span> {formatCurrency(subtotals.estimatedGross)}</span>
            <span><span className="text-gray-500">Actual Gross:</span> {formatCurrency(subtotals.actualGross)}</span>
            <span><span className="text-gray-500">F&amp;I{excludeFiReserve ? ' (net)' : ''}:</span> {formatCurrency(subtotals.fAndI)}</span>
            <span><span className="text-gray-500">Back End $:</span> {formatCurrency(subtotals.points)}</span>
            <span><span className="text-gray-500">Holdback:</span> {formatCurrency(subtotals.holdback)}</span>
            <span><span className="text-gray-500">Doc:</span> {formatCurrency(subtotals.doc)}</span>
            <span className="border-l border-gray-300 pl-5 text-base text-blue-700">
              <span className="text-gray-500">Today&apos;s Total:</span>{' '}
              {formatCurrency(
                subtotals.actualGross +
                  subtotals.fAndI +
                  subtotals.holdback +
                  subtotals.doc,
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
